import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardDrive } from "lucide-react";
import api, { formatBytes } from "../lib/api";
import { useIsMobile } from "../hooks/useViewport";

// Lightweight "storage used vs included" indicator for the top-right of the app shell.
// On mobile it stays out of the way until storage actually needs attention.
export default function StorageIndicator() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);

  const load = () => api.get("/storage").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const onUpdate = () => load();
    window.addEventListener("storage-updated", onUpdate);
    return () => window.removeEventListener("storage-updated", onUpdate);
  }, []);

  if (!data || !data.quota_bytes) return null;
  const pct = Math.min(100, Math.round((data.used_bytes / data.quota_bytes) * 100));
  const over = data.over_allowance;
  const near = !over && pct >= 85;
  if (isMobile && !over && pct < 70) return null;
  const barColour = over ? "bg-[#EF4444]" : near ? "bg-[#F59E0B]" : "bg-[#0F766E]";
  const ringColour = over ? "text-[#EF4444]" : near ? "text-[#B45309]" : "text-[#0F766E]";

  return (
    <button
      data-testid="storage-indicator"
      onClick={() => navigate("/app/settings?section=storage")}
      title={`${formatBytes(data.used_bytes)} of ${formatBytes(data.quota_bytes)} used`}
      className="group flex items-center gap-2.5 rounded-full border border-[#E2E8F0] bg-white/80 px-3 py-1.5 text-left transition-colors hover:border-[#0F766E]/40 hover:bg-white"
    >
      <HardDrive className={`h-4 w-4 shrink-0 ${ringColour}`} strokeWidth={1.8} aria-hidden="true" />
      <div className="hidden sm:block">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium text-[#2D3436]" data-testid="storage-indicator-label">
            {formatBytes(data.used_bytes)} <span className="text-[#9aa3a0]">/ {formatBytes(data.quota_bytes)}</span>
          </span>
        </div>
        <div className="mt-1 h-1 w-28 overflow-hidden rounded-full bg-[#f0f1ee]">
          <div className={`h-full rounded-full ${barColour} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="sm:hidden text-[11px] font-medium text-[#2D3436]">{pct}%</span>
    </button>
  );
}
